import {
	HttpClient,
	HttpResponse
}                             from '@angular/common/http';
import { Injectable }         from '@angular/core';
import { AbstractApiService } from '@app/core/http/abstract-api.service';
import { SanitizerService }   from '@app/core/http/sanitizer.service';
import { TransitionService }  from '@app/core/http/transition.service';
import {
	IArchivingImportHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState
}                             from '@app/core/interfaces/api.interface';
import { IList }              from '@app/core/interfaces/list-response.interfaces';
import { ModelTypeEnum }      from '@app/core/models/transition.model';
import {
	ArchivingImport,
	ArchivingImportStatusEnum
}                             from '@app/modules/archiving/models/archiving-import.model';
import { LoggerService }      from '@app/shared/services/logger.service';
import {
	ClsImportRequestDTO,
	DeleteImportRequestParams,
	ImportDTO,
	ImportRequestDTO,
	ImportsApiClient,
	ImportStatusEnumDTO,
	ListImportsRequestParams,
	ReadImportRequestParams
}                             from '@openApiGenerated/archiving-api';
import { Observable }         from 'rxjs';

@Injectable( {
	providedIn: 'root'
} )
export class ArchivingImportApiService extends AbstractApiService {

	constructor(
		protected http: HttpClient,
		protected logger: LoggerService,
		private readonly importsApiImport: ImportsApiClient,
		private readonly sanitize: SanitizerService,
		private readonly transition: TransitionService
	) {
		super();
		this.http = http;
	}


	// SANITIZER ######################################
	private sanitizeApiResponse( entity: ArchivingImport, context = this ): void {
		if ( entity instanceof ArchivingImport ) {
			entity.created = context.sanitize.date<Date>( entity.created, ModelTypeEnum.TMO, Date );
			entity.status = context.mapDtoStatusToTmo( entity.status as unknown as ImportStatusEnumDTO );
		}
	}

	private sanitizeForApiRequest( dto: ImportRequestDTO, context = this ): void {
		if ( dto instanceof ClsImportRequestDTO ) {
		}
	}


	// SPECIFIC MAPPER ################################
	private mapTmoStatusListToDto( tmoStatusList: ArchivingImportStatusEnum[] ): ImportStatusEnumDTO[] {
		return tmoStatusList?.map( ( archivingImportStatusEnum: ArchivingImportStatusEnum ) =>
			this.mapTmoStatusToDto( archivingImportStatusEnum )
		);
	}

	private mapTmoStatusToDto( tmoStatus: ArchivingImportStatusEnum ): ImportStatusEnumDTO {

		switch ( tmoStatus ) {
			case ArchivingImportStatusEnum.BEING_REPLACED:
				return ImportStatusEnumDTO.BEING_REPLACED;

			case ArchivingImportStatusEnum.ERROR:
				return ImportStatusEnumDTO.ERROR;

			case ArchivingImportStatusEnum.LOCKED:
				return ImportStatusEnumDTO.LOCKED;

			case ArchivingImportStatusEnum.OPEN:
				return ImportStatusEnumDTO.OPEN;

			case ArchivingImportStatusEnum.REPLACED:
				return ImportStatusEnumDTO.REPLACED;
		}
	}

	private mapDtoStatusToTmo( dtoStatus: ImportStatusEnumDTO ): ArchivingImportStatusEnum {
		switch ( dtoStatus ) {
			case ImportStatusEnumDTO.BEING_REPLACED:
				return ArchivingImportStatusEnum.BEING_REPLACED;

			case ImportStatusEnumDTO.ERROR:
				return ArchivingImportStatusEnum.ERROR;

			case ImportStatusEnumDTO.LOCKED:
				return ArchivingImportStatusEnum.LOCKED;

			case ImportStatusEnumDTO.OPEN:
				return ArchivingImportStatusEnum.OPEN;

			case ImportStatusEnumDTO.REPLACED:
				return ArchivingImportStatusEnum.REPLACED;
		}
	}

	// LIST TYPE REQUESTS #############################
	public search( params?: IArchivingImportHttpParams, throwError = false ): Observable<RequestCompleteListState<ArchivingImport>> {
		this.logger.debug( 'pre-list call' );

		const requestParam: ListImportsRequestParams = {
			businessPremisesId: params.businessPremisesId,
			pageSize: params?.paging?.pageSize,
			pageNumber: params?.paging?.pageNumber,
			businessDateFrom: params?.businessDateFrom,
			businessDateTo: params?.businessDateTo,
			createdDateFrom: params?.createdDateFrom,
			createdDateTo: params?.createdDateTo,
			sort: params?.sort,
			status: this.mapTmoStatusListToDto( params?.status ),
			ersId: params?.ersId?.length ? params?.ersId[ 0 ] : null
		};

		const req$ = this.importsApiImport.listImports( requestParam );

		return this.transition.handleListRequestResponse<ImportDTO, ArchivingImport>(
			req$ as Observable<IList<ImportDTO>>,
			ArchivingImport,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	// ENTITY TYPE REQUESTS ###########################
	public read( id: string, withFullResponse: boolean = true, throwError = false ): Observable<RequestCompleteEntityState<ArchivingImport>> {
		this.logger.debug( 'pre-read call: ', id );
		const requestParam: ReadImportRequestParams = { importId: id };

		const req$ = this.importsApiImport.readImport( requestParam, withFullResponse ? 'response' : undefined );
		return this.transition.handleEntityRequestResponse<ImportDTO | HttpResponse<ImportDTO>, ArchivingImport>(
			req$,
			RequestActionEnum.READ,
			ArchivingImport,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public delete( archivingImportId: string, throwError = false ): Observable<RequestCompleteEntityState<ArchivingImport>> {
		this.logger.debug( 'pre-delete call: ', archivingImportId );
		const requestParam: DeleteImportRequestParams = {
			importId: archivingImportId
		};

		const req$ = this.importsApiImport.deleteImport( requestParam, 'response' );
		return this.transition.handleEntityRequestResponse<HttpResponse<any>, ArchivingImport>(
			req$,
			RequestActionEnum.DELETE,
			ArchivingImport,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}
}
